import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, MapPin, Download, Wifi, WifiOff, Heart, Flashlight, Navigation } from 'lucide-react';

export default function CrisisWelcomeModal({ open, onClose, onStartSync, c1, c2, hasSynced }) {
  const [step, setStep] = useState(0); // 0 = intro, 1 = download options

  const handleDismiss = () => {
    onClose();
    setStep(0);
  };

  const handleNext = () => {
    if (hasSynced) {
      handleDismiss();
    } else {
      setStep(1);
    }
  };

  const handleSync = () => {
    handleDismiss();
    onStartSync();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="sm:max-w-md bg-background border-border/50" data-testid="crisis-welcome-modal">
        {step === 0 ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5" style={{ color: c1 }} />
                <DialogTitle className="text-xl font-bold">
                  <span className="text-foreground">CRISIS</span>
                  <span style={{ color: c1 }}> ZONE</span>
                </DialogTitle>
              </div>
              <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
                A community-powered emergency resource map for the UK.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: c2 }} />
                <p className="text-sm text-foreground/90">
                  <strong>Find help nearby</strong> — Water points, food banks, shelters, charging stations, medical facilities and more.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <WifiOff className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: c2 }} />
                <p className="text-sm text-foreground/90">
                  <strong>Works offline</strong> — Download the UK road network and navigate without internet. Road-by-road turn directions, just like a sat nav.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Navigation className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: c2 }} />
                <p className="text-sm text-foreground/90">
                  <strong>Drive, cycle, or walk</strong> — Choose your transport mode. Lane guidance included for major roads.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Heart className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: c2 }} />
                <p className="text-sm text-foreground/90">
                  <strong>Each one help one</strong> — Post what you need or what you can offer on the community notice board.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Flashlight className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: c2 }} />
                <p className="text-sm text-foreground/90">
                  <strong>SOS Signal</strong> — Flash your phone torch or screen in Morse code. Send custom messages too.
                </p>
              </div>
            </div>
            <Button
              onClick={handleNext}
              className="w-full h-11 text-white font-semibold rounded-xl mt-1"
              style={{ backgroundColor: c1 }}
              data-testid="crisis-welcome-next-btn"
            >
              {hasSynced ? 'Got it' : 'Next — Offline Setup'}
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Download className="w-5 h-5" style={{ color: c1 }} />
                <DialogTitle className="text-lg font-bold text-foreground">Download for Offline Use</DialogTitle>
              </div>
              <DialogDescription className="text-muted-foreground text-sm">
                To navigate without internet, we need to download some data to your device.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="rounded-xl border border-border/40 bg-card/50 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Wifi className="w-4 h-4" style={{ color: c2 }} />
                  <p className="text-sm font-semibold text-foreground">What gets downloaded:</p>
                </div>
                <ul className="text-xs text-muted-foreground space-y-1.5 ml-6">
                  <li>Map tiles for your area (~50-150 MB)</li>
                  <li>UK road network for navigation (~175 MB)</li>
                  <li>1.75 million UK postcodes (~15 MB)</li>
                  <li>Cached routes to nearby resources</li>
                </ul>
                <p className="text-[10px] text-muted-foreground/70 ml-6">
                  Total: ~240-340 MB. Use Wi-Fi for best results.
                </p>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                You can skip this and download later from the sync button.
              </p>
            </div>
            <div className="flex gap-2 mt-1">
              <Button
                variant="outline"
                onClick={handleDismiss}
                className="flex-1 h-11 rounded-xl text-sm border-border text-muted-foreground"
                data-testid="crisis-welcome-skip-btn"
              >
                Skip for now
              </Button>
              <Button
                onClick={handleSync}
                className="flex-1 h-11 text-white font-semibold rounded-xl text-sm"
                style={{ backgroundColor: c1 }}
                data-testid="crisis-welcome-download-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Download Now
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
